import React from 'react';
import { Props } from '.';
import Icon from '../../../components/icon/Icon';
import { ClockState } from '../../../core/models/enum';
import { EventTypeEnum } from '../../../utils/api';
import { splitWholeSeconds } from '../../../helpers/countdownClock';
import { periodPills } from '../../../helpers/matchTiming';
import LiveBroadcastIcon from './LiveBroadcastIcon';

class MatchClock extends React.PureComponent<Props> {
    // Saving is non-blocking: the check just hands the event to the background
    // queue, which reports loading/success/error in a corner notification.
    private onConfirmation = (): void => {
        this.props.saveEvent();
    };

    public render = (): React.ReactElement => {
        const {
            event,
            liveEnabled,
            liveSnapshot,
            timeLocked,
            draftTimeVerified,
            clockState,
            eventTime,
            eventPeriod,
            overtimeUiMode,
            isEventValid,
            startClock,
            stopClock,
            setPeriod,
            modifyClock,
            periodCount,
        } = this.props;

        // Fraction stays in state for accurate start/stop; UI shows whole seconds.
        const { minute: clockMinutes, seconds: clockSeconds } = splitWholeSeconds(eventTime);
        const clockMinutesDisplay = clockMinutes < 10 ? `0${clockMinutes}` : clockMinutes;
        const clockSecondsDisplay = clockSeconds < 10 ? `0${clockSeconds}` : clockSeconds;

        let className = '';

        switch (clockState) {
            case ClockState.Running:
                className = 'is-running';
                break;
            case ClockState.Paused:
            case ClockState.Stopped:
                className = isEventValid ? 'is-time-confirmation-active' : 'is-paused';
                break;
            default:
                break;
        }
        const stopwatchClickHandler =
            clockState === ClockState.Running ? (): void => stopClock() : (): void => startClock();
        const goalkeeperNeedsANotice =
            !!event && event.typeId === EventTypeEnum.Goalkeeper_Change && !isEventValid;
        const confirmClick = draftTimeVerified !== false && (isEventValid || goalkeeperNeedsANotice)
            ? (): void => this.onConfirmation()
            : undefined;
        const isEditingEvent = event != null;
        const canModifyTime = clockState !== ClockState.Running && (!liveEnabled || isEditingEvent) && !timeLocked;
        const pills = periodPills(periodCount);
        const regulationPills = pills.filter((pill) => pill.overtimeMode == null);
        const extraPills = pills.filter((pill) => pill.overtimeMode != null);

        return (
            <div className="o-clock-screen noselect">
                <div className={`c-clock ${className}`}>
                    <div className="c-clock__period-panel">
                        <div
                            className="c-clock__period-pills-wrap"
                            role="group"
                            aria-label="Period for the event being recorded"
                        >
                            <div className="c-clock__period-pills c-clock__period-pills--regular">
                                {regulationPills.map((pill) => (
                                    <span
                                        key={pill.periodId}
                                        onClick={(): void => setPeriod(pill.periodId)}
                                        aria-disabled={liveEnabled && isEditingEvent}
                                        className={
                                            eventPeriod === pill.periodId ? 'is-active' : ''
                                        }
                                        role="button"
                                        title={pill.label}
                                    >
                                        {pill.label}
                                    </span>
                                ))}
                            </div>
                            <div className="c-clock__period-divider" aria-hidden="true" />
                            <div className="c-clock__period-ot">
                                {extraPills.map((pill) => (
                                    <span
                                        key={`${pill.label}-${pill.overtimeMode}`}
                                        onClick={(): void =>
                                            setPeriod(pill.periodId, pill.overtimeMode)
                                        }
                                        className={
                                            eventPeriod === pill.periodId &&
                                            overtimeUiMode === pill.overtimeMode
                                                ? 'is-active'
                                                : ''
                                        }
                                        role="button"
                                        title={
                                            pill.overtimeMode === 'penalty_shootout'
                                                ? 'Penalty shootout (same period as OT)'
                                                : 'Overtime'
                                        }
                                    >
                                        {pill.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`c-clock__clock ${
                            clockState !== ClockState.Running ? 'enabled' : ''
                        }`}
                    >
                        <div
                            className={`c-clock__pause${liveEnabled ? ' c-clock__pause--live' : ''}`}
                            onClick={isEditingEvent || liveEnabled ? undefined : stopwatchClickHandler}
                            aria-disabled={liveEnabled ? undefined : isEditingEvent}
                            role={liveEnabled ? 'status' : 'button'}
                        >
                            {liveEnabled ? (
                                liveSnapshot?.eligible ? <LiveBroadcastIcon /> : <span>{liveSnapshot?.status}</span>
                            ) : <div className="c-clock__pause-inner">
                                <Icon name="icon-stopwatch" />
                            </div>}
                        </div>
                        <div className="c-clock__minutes">
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(60)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(-60)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                            <div className="c-clock__digits">{clockMinutesDisplay}</div>
                        </div>
                        <div className="c-clock__colon">:</div>
                        <div className="c-clock__seconds">
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(10)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(-10)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                            <div className="c-clock__digits">{clockSecondsDisplay}</div>
                            <div className="c-clock__minus-plus">
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(1)
                                            : undefined
                                    }
                                    className="c-clock__plus"
                                    role="button"
                                >
                                    <Icon name="icon-plus" />
                                </span>
                                <span
                                    onClick={
                                        canModifyTime
                                            ? (): void => modifyClock(-1)
                                            : undefined
                                    }
                                    className="c-clock__minus"
                                    role="button"
                                >
                                    <Icon name="icon-minus" />
                                </span>
                            </div>
                        </div>
                        <div
                            className="c-clock__confirmation"
                            onClick={confirmClick}
                            role="button"
                        >
                            <Icon name="icon-check" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };
}

export default MatchClock;
